/**
 * Estimate Detail Page - T182
 *
 * View and manage a single estimate
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, LoadingSpinner } from '../components/ui';
import { EstimateStatusBadge } from '../components/estimates/EstimateStatusBadge';
import { ConvertToInvoiceButton } from '../components/estimates/ConvertToInvoiceButton';
import { useEstimate, useSendEstimate, useAcceptEstimate, useRejectEstimate, getEstimatePdfUrl } from '../hooks/useEstimates';
import { formatCurrency, formatDate } from '@armard/shared';
import { useToastHelpers } from '../components/ui';

export const Route = createFileRoute('/estimates_/$id')({
  component: EstimateDetailPage,
});

function EstimateDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();

  const { data: estimate, isLoading, error } = useEstimate(id);
  const sendEstimate = useSendEstimate();
  const acceptEstimate = useAcceptEstimate();
  const rejectEstimate = useRejectEstimate();

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !estimate) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="text-center py-12">
            <h2 className="text-lg text-red-400">Angebot nicht gefunden</h2>
            <Button className="mt-4" onClick={() => navigate({ to: '/estimates' })}>
              Zurück zur Liste
            </Button>
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  // Calculate totals
  const items = estimate.items || [];
  const netTotal = parseFloat(estimate.netTotal);
  const vatAmount = parseFloat(estimate.vatAmount);
  const grossTotal = parseFloat(estimate.grossTotal);
  const vatRate = parseFloat(estimate.vatRate);

  const handleSend = async () => {
    try {
      await sendEstimate.mutateAsync(estimate.id);
      toast.success('Angebot als versendet markiert');
    } catch {
      toast.error('Fehler beim Markieren');
    }
  };

  const handleAccept = async () => {
    try {
      await acceptEstimate.mutateAsync(estimate.id);
      toast.success('Angebot als angenommen markiert');
    } catch {
      toast.error('Fehler beim Markieren');
    }
  };

  const handleReject = async () => {
    try {
      await rejectEstimate.mutateAsync(estimate.id);
      toast.success('Angebot als abgelehnt markiert');
    } catch {
      toast.error('Fehler beim Markieren');
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-white">{estimate.estimateNumber}</h1>
              <EstimateStatusBadge status={estimate.status} />
            </div>
            {estimate.subject && (
              <p className="text-white/70">{estimate.subject}</p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {estimate.status === 'draft' && (
              <Button onClick={handleSend} disabled={sendEstimate.isPending}>
                Als versendet markieren
              </Button>
            )}
            {estimate.status === 'sent' && (
              <>
                <Button onClick={handleAccept} disabled={acceptEstimate.isPending}>
                  Angenommen
                </Button>
                <Button variant="secondary" onClick={handleReject} disabled={rejectEstimate.isPending}>
                  Abgelehnt
                </Button>
              </>
            )}
            <ConvertToInvoiceButton estimate={estimate} />
            <a href={getEstimatePdfUrl(estimate.id)} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">PDF anzeigen</Button>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer & Dates */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-white/40">Kunde</p>
                  <p className="text-white">{estimate.customer?.companyName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Kundennummer</p>
                  <p className="text-white">{estimate.customer?.customerNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Erstelldatum</p>
                  <p className="text-white">{formatDate(estimate.issueDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Gültig bis</p>
                  <p className="text-white">{estimate.validUntil ? formatDate(estimate.validUntil) : 'Unbefristet'}</p>
                </div>
                {estimate.customerReference && (
                  <div className="col-span-2">
                    <p className="text-sm text-white/40">Kundenreferenz</p>
                    <p className="text-white">{estimate.customerReference}</p>
                  </div>
                )}
              </div>
            </GlassPanel>

            {/* Intro Text */}
            {estimate.introText && (
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Einleitungstext</h2>
                <p className="text-white/70 whitespace-pre-wrap">{estimate.introText}</p>
              </GlassPanel>
            )}

            {/* Items */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Positionen</h2>
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start justify-between p-3 bg-white/5 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="text-white">{item.description}</p>
                      {item.longDescription && (
                        <p className="text-sm text-white/50 mt-1">{item.longDescription}</p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-white">
                        {parseFloat(item.quantity)} {item.unit} × {formatCurrency(parseFloat(item.unitPrice))}
                      </p>
                      <p className="text-white/70">
                        {formatCurrency(parseFloat(item.total))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Netto</span>
                  <span className="text-white">{formatCurrency(netTotal)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-white/50">MwSt. ({vatRate}%)</span>
                  <span className="text-white">{formatCurrency(vatAmount)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold mt-2">
                  <span className="text-white">Gesamt</span>
                  <span className="text-white">{formatCurrency(grossTotal)}</span>
                </div>
              </div>
            </GlassPanel>

            {/* Closing Text */}
            {estimate.closingText && (
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Schlusstext</h2>
                <p className="text-white/70 whitespace-pre-wrap">{estimate.closingText}</p>
              </GlassPanel>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Info */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Status</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-white/40">Aktueller Status</p>
                  <EstimateStatusBadge status={estimate.status} />
                </div>
                {estimate.sentAt && (
                  <div>
                    <p className="text-sm text-white/40">Versendet am</p>
                    <p className="text-white">{formatDate(estimate.sentAt)}</p>
                  </div>
                )}
                {estimate.acceptedAt && (
                  <div>
                    <p className="text-sm text-white/40">Angenommen am</p>
                    <p className="text-white">{formatDate(estimate.acceptedAt)}</p>
                  </div>
                )}
                {estimate.invoiceId && (
                  <div>
                    <p className="text-sm text-white/40">Umgewandelt in</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate({ to: '/invoices/$id', params: { id: estimate.invoiceId! } })}
                    >
                      Zur Rechnung
                    </Button>
                  </div>
                )}
              </div>
            </GlassPanel>

            {/* Quick Actions */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Aktionen</h2>
              <div className="space-y-2">
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/estimates' })}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Zur Übersicht
                </Button>
              </div>
            </GlassPanel>
          </div>
        </div>
      </AppShell>
    </ProtectedRoute>
  );
}
